/**
 * App.js — thin Router shell.
 *
 * The bulk of the application UI lives in MainApp.js. This file only owns:
 *   • Top-level imports for routes
 *   • The ErrorBoundary class
 *   • The AppWithErrorBoundary + AppWithAuth wrappers + Router config
 *
 * If you're looking for state, analyze logic, or UI cards — open MainApp.js.
 */
import React, { useState, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DemoAccountGate } from './components/DemoAccountGate';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const UnionMetalAnalysis = lazy(() => import('./components/admin/UnionMetalAnalysis'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const EPATablesPage = lazy(() => import('./components/EPATablesPage'));
const EPAUpdatePage = lazy(() => import('./components/EPAUpdatePage'));
const BookDemoPage = lazy(() => import('./components/BookDemoPage'));
const PgVideoPage = lazy(() => import('./components/PgVideoPage'));
const PgLiveDemo = lazy(() => import('./components/PgLiveDemo'));
const SharedValidationPage = lazy(() => import('./components/validation/SharedValidationPage'));
const SealReviewPage = lazy(() => import('./components/SealReviewPage'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const LegalDocPage = lazy(() => import('./components/legal/LegalDocPage'));
import TermsGate from './components/legal/TermsGate';
const DemoManagePage = lazy(() => import('./components/DemoManagePage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const FieldCheck = lazy(() => import('./components/FieldCheck'));
const ElevatorPortalPage = lazy(() => import('./components/ElevatorPortalPage'));
const MainApp = lazy(() => import('./MainApp'));
const FixtureLibraryPage = lazy(() => import('./components/fixtures/FixtureLibraryPage'));
const TankOnlyApp = lazy(() => import('./components/TankOnlyApp'));
import './App.css';
import { Toaster } from 'sonner';
import { LabQuestionsPrompt } from './components/LabQuestionsPrompt';

const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loading">
    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Runtime Error Detected</h1>
            <div className="text-sm text-gray-700 mb-4">
              <strong>Error:</strong> {this.state.error && this.state.error.toString()}
            </div>
            {this.state.errorInfo && this.state.errorInfo.componentStack && (
              <div className="text-xs text-gray-600 bg-gray-100 p-4 rounded">
                <strong>Stack Trace:</strong>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </div>
            )}
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppWithErrorBoundary = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppWithAuth />
      </AuthProvider>
    </ErrorBoundary>
  );
};

const ToLogin = ({ from }) => {
  sessionStorage.setItem('post_login', from + (window.location.pathname === from ? window.location.search : ''));
  return <Navigate to="/login" replace />;
};

const postLoginPath = (user) => {
  const p = sessionStorage.getItem('post_login');
  return p || (user?.company === 'County Prestress & Precast' ? '/elevator-shafts' : '/app');
};

const AppWithAuth = () => {
  const { isAuthenticated, loading, isDemoMode, isAdmin, isTankOnly, logout, user } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(() => { const q = new URLSearchParams(window.location.search); return q.has('sg_session') || q.has('lab_questions') || q.has('outlook'); });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Toaster position="top-right" richColors />
      {isAuthenticated && !isAdmin && /county prestress/i.test(user?.company || '') && <LabQuestionsPrompt email={user?.email} />}
      <TermsGate />
      <DemoAccountGate />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Landing page - accessible to everyone */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Terms of Service and Privacy Policy - accessible to everyone */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/saas-agreement" element={<LegalDocPage doc="saas" />} />
        <Route path="/fixture-library" element={<FixtureLibraryPage />} />
        <Route path="/book-demo" element={<BookDemoPage />} />
        <Route path="/pg-video" element={<PgVideoPage />} />
        <Route path="/pg-live" element={<PgLiveDemo />} />
        <Route path="/demo/manage" element={<DemoManagePage />} />
        <Route path="/validation/shared/:token" element={<SharedValidationPage />} />
        <Route path="/seal-review/:token" element={<SealReviewPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Payment routes */}
        <Route 
          path="/payment/success" 
          element={
            <PaymentSuccess 
              onComplete={(sessionId, status) => {
                // Redirect back to app to continue with sealed document request
                window.location.href = '/app?payment_complete=true&session_id=' + sessionId;
              }}
            />
          } 
        />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        
        {/* Login page */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? (
              <Navigate to={postLoginPath(user)} replace />
            ) : (
              <LoginPage onLoginSuccess={() => setShowLogin(false)} />
            )
          } 
        />

        {/* Password reset landing (from forgot-password email) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Main app - requires authentication */}
        <Route 
          path="/app" 
          element={
            isAuthenticated ? (
              user?.company === 'County Prestress & Precast' && !isAdmin && !new URLSearchParams(window.location.search).has('session_id') ? (
                <Navigate to="/elevator-shafts" replace />
              ) : isTankOnly ? (
                <TankOnlyApp />
              ) : (
                <>
                  <MainApp 
                    isAuthenticated={isAuthenticated}
                    isDemoMode={isDemoMode}
                    isAdmin={isAdmin}
                    onShowLogin={() => setShowLogin(true)}
                    onShowAdmin={() => setShowAdminPanel(true)}
                    onLogout={logout}
                  />
                  {showAdminPanel && isAdmin && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
                </>
              )
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        {/* Union Metal standalone window */}
        <Route 
          path="/union-metal" 
          element={
            isAuthenticated ? (
              <div className="min-h-screen bg-gray-50">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
                  <h1 className="text-2xl font-bold">Union Metal Analysis</h1>
                  <p className="text-sm text-blue-100">Mast arm and pole design per AASHTO LRFDLTS-1</p>
                </div>
                <div className="p-6">
                  <UnionMetalAnalysis />
                </div>
              </div>
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        <Route path="/epa-update" element={isAuthenticated ? <EPAUpdatePage /> : <Navigate to="/login" replace />} />

        {/* EPA Tables - Max EPA by manufacturer */}
        <Route 
          path="/epa-tables" 
          element={
            isAuthenticated ? (
              <EPATablesPage />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />

        {/* Field Check — mobile-friendly quick analyzer */}
        <Route
          path="/field-check"
          element={
            isAuthenticated ? (
              <FieldCheck />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/elevator-shafts"
          element={isAuthenticated ? <ElevatorPortalPage /> : <ToLogin from="/elevator-shafts" />}
        />

        {/* Catch all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
  );
};

export default AppWithErrorBoundary;
